import React from 'react';

/**
 * One colour set for every document status in purchasing:
 * green done · amber partial · blue in progress · red stopped · grey draft.
 */
export type Tone = 'ok' | 'warn' | 'info' | 'bad' | 'mute' | 'wait' | 'progress';

const TONES: Record<Tone, { bg: string; fg: string; border?: string }> = {
  ok: { bg: '#e4f5ea', fg: '#1a7f4b' },
  warn: { bg: '#fff3d6', fg: '#9a5b00' },
  info: { bg: '#e6efff', fg: '#1e5fb8' },
  bad: { bg: '#fde8e8', fg: '#b42323' },
  mute: { bg: '#f5f6f8', fg: '#4b5563', border: '#e3e6ec' },
  wait: { bg: '#f3e8ff', fg: '#6b21a8' }, // violet: waiting for someone's decision
  progress: { bg: '#0e6b7a', fg: '#ffffff' }, // solid dark teal: with the vendor / in transit — must not read as the blue of Approved
};

const STATUS_TONE: Record<string, Tone> = {
  // MR
  // MR lifecycle reads left→right as grey · violet · blue · amber · teal · amber · green, red for rejected.
  Draft: 'mute',
  Submitted: 'wait',
  Approved: 'info',
  'Partially Ordered': 'warn',
  Ordered: 'progress',
  'Partially Received': 'warn',
  Received: 'ok',
  Rejected: 'bad',
  // PO
  Open: 'info',
  Closed: 'ok',
  Cancelled: 'bad',
  // GRN / returns / vendors / lines
  Pending: 'wait',
  Shipped: 'progress',
  'Pending Cancellation': 'warn',
  Active: 'ok',
  Inactive: 'mute',
  Requested: 'info',
  'Sent to vendor': 'warn',
  Credited: 'ok',
  Completed: 'ok',
  Refunded: 'ok',
  Merged: 'mute',
};

export function toneOfStatus(status?: string | null): Tone {
  if (!status) return 'mute';
  return STATUS_TONE[status] ?? 'mute';
}

export interface StatusPillProps {
  status?: string | null;
  tone?: Tone;
  label?: React.ReactNode;
}

const StatusPill: React.FC<StatusPillProps> = ({ status, tone, label }) => {
  const t = TONES[tone ?? toneOfStatus(status)];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        background: t.bg,
        color: t.fg,
        border: t.border ? `1px solid ${t.border}` : undefined,
      }}
    >
      {label ?? status ?? '—'}
    </span>
  );
};

/** Urgency word in colour, no pill: CRITICAL red, HIGH amber, Normal muted. */
export const UrgencyText: React.FC<{ urgency?: string | null }> = ({ urgency }) => {
  if (!urgency) return null;
  const u = urgency.toLowerCase();
  const style: React.CSSProperties =
    u === 'critical' ? { color: '#8a1c1c', fontWeight: 600, letterSpacing: '.03em' }
    : u === 'high' ? { color: '#9a5b00', fontWeight: 600, letterSpacing: '.03em' }
    : { color: '#8a94a6', fontWeight: 500 };
  return <span style={{ fontSize: 11.5, ...style }}>{u === 'critical' || u === 'high' ? urgency.toUpperCase() : urgency}</span>;
};

export default StatusPill;
